import { Component, Element, Fragment, Host, Prop, State, h } from '@stencil/core';

import { onAppointmentRequestSelected } from '../../../actions/actions.dealer';
import { handleComponentScroll } from '../../../actions/actions.funnel-component';
import { ScrollMilestones } from '../../../interfaces/interface.offer';
import { AutoCheckState } from '../../../store/store.autocheck';
import { ConsumerMarketConfidenceState, initialState as consumerMarketConfidenceInitialState } from '../../../store/store.consumer-market-confidence';
import { CounterofferState, initialState as counterofferInitialState } from '../../../store/store.counteroffer';
import { KelleyBuysYourCarOfferState, initialState as kelleyBuysYourCarOfferInitialState } from '../../../store/store.kelley-buys-your-car-offer';
import { OfferState, initialState } from '../../../store/store.offer';
import { trackAppointmentRequestTopClick } from '../../../tracking/tracking.dealer';
import { DISPOSAL_TYPES } from '../../../utils/utils.offer';
import { OfferDetailsModal } from '../offer-details-modal/offer-details-modal';

@Component({
  tag: 'offer-certificate-valid',
  styleUrl: 'offer-certificate-valid.css',
})
export class OfferCertificateValid {
  @Element() el: HTMLElement;
  dealerListElem!: HTMLElement;

  @State() scrollMilestones: ScrollMilestones = {
    25: false,
    50: false,
    75: false,
    100: false,
  };

  @Prop() autoCheck: AutoCheckState;

  @Prop() customer = { email: '', firstName: '', lastName: '', phone: '', zip: '' };
  @Prop() isAdmin: boolean = false;
  @Prop() theme: string = '';

  @Prop() dealer = {
    name: '',
    isOfficeClosed: false,
    allocated: [],
    dealerOffers: {},
    buyingCenters: [],
    numAssignableDealers: 0,
    dealerAppointmentInfo: null,
  };

  @Prop() offer: OfferState = initialState;

  @Prop() vehicle = {
    accidents: false,
    auctionedRecently: false,
    color: '',
    description: '',
    drivetrain: '',
    engine: '',
    fullName: '',
    hasReplacement: false,
    hasTwoSetsOfKeys: true,
    imageUrl: '',
    offerOptions: [],
    isAuctionedLast45: false,
    isRental: false,
    mileage: 0,
    originalOwner: false,
    rentalVehicle: false,
    replacementName: '',
    replacementText: '',
    replacementTypes: '',
    replacementVehicleYearMax: 2023,
    replacementVehicleYearMin: 2018,
    transmission: '',
    vin: '',
    yearMakeModel: '',
  };

  @Prop() optimizely = {
    icocertenhancementsOptimizely: false,
    isConsumerMarketConfidenceOn: false,
    isAppleWalletPassEnabled: false,
  };

  @Prop() showVehicleDetailDialog: (e: MouseEvent) => void = () => {};

  @Prop() acceptOrCounteroffer: CounterofferState = counterofferInitialState;
  @Prop() kelleyBuysYourCarOffer: KelleyBuysYourCarOfferState = kelleyBuysYourCarOfferInitialState;
  @Prop() consumerMarketConfidence: ConsumerMarketConfidenceState = consumerMarketConfidenceInitialState;

  componentDidLoad() {
    this.addScrollListener();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  addScrollListener = () => {
    window.addEventListener('scroll', this.handleScroll);
  };

  handleScroll = () => {
    handleComponentScroll(this.el, this.scrollMilestones, (newMilestones) => {
      this.scrollMilestones = newMilestones;
    });
  };

  handleAppointmentRequestClick = () => {
    onAppointmentRequestSelected({ offerId: this.offer.offerId });
    trackAppointmentRequestTopClick();
    this.dealerListElem.scrollIntoView();
  };

  render() {
    const { consumerMarketConfidence } = this;
    const { shouldDisplayExpectDealership, shouldDisplayPrepareForVisit, shouldDisplayConfidentVehicleHistoryReport, shouldDisplayDealerListInfo } = consumerMarketConfidence;
    const { autoCheckFullReportLink, reportDate, historyIcons } = this.autoCheck;
    const { email, firstName, zip } = this.customer;
    const { allocated, dealerOffers, buyingCenters, name, dealerAppointmentInfo } = this.dealer;
    const { amount, dealerBoostAmount, expirationDate, offerId, formattedFinalOfferAmount, potentialTaxSaving, showDealerChosenReasons, isKelleyBuysYourCar, disposalType } =
      this.offer;
    const { fullName, imageUrl, vin } = this.vehicle;

    const hasDealerBoost = dealerBoostAmount > 0;

    const canRequestOfferPrice = this.dealer.numAssignableDealers && this.offer.displayRequestAppointmentButton;
    const showKelleyBuysYourCar = isKelleyBuysYourCar;
    const shouldDisplayTaxSavings = potentialTaxSaving > 0 && !consumerMarketConfidence.shouldDisplayAdditionalInsights;
    const historyReportClass = {
      'grid gap-7': true,
      'md:grid-cols-2 mb-8': shouldDisplayConfidentVehicleHistoryReport,
    };
    const { isAppleWalletPassEnabled } = this.optimizely;

    return (
      <Host>
        <certificate-layout showKelleyBuysYourCar={showKelleyBuysYourCar}>
          {!showKelleyBuysYourCar && hasDealerBoost && <dealer-boost-box dealerName={name} boost={this.offer.dealerBoostAmount} slot="hero" />}

          <congratulation-certificate-message
            consumer={firstName}
            dealer={name}
            vehicleImageLocation={imageUrl}
            vehicle={fullName}
            whenReviewClicked={this.showVehicleDetailDialog.bind(this)}
            slot="left"
            theme={this.theme}
            showKelleyBuysYourCar={showKelleyBuysYourCar}
            offerId={offerId}
            zip={zip}
            email={email}
          />

          {!showKelleyBuysYourCar && hasDealerBoost && <dealer-boost-details-box dealerName={name} boost={this.offer.dealerBoostAmount} initialOfferAmount={amount} slot="left" />}

          {amount > 0 && (
            <offer-price
              showOfferPriceButton={canRequestOfferPrice}
              email={email}
              expirationDate={expirationDate}
              hasDealerBoost={hasDealerBoost}
              offerAmount={formattedFinalOfferAmount}
              offerId={offerId}
              offerPriceButtonClick={this.handleAppointmentRequestClick}
              buttonText="Request Appointment"
              zip={zip}
              slot="right"
              showFinalActionButton={!this.acceptOrCounteroffer.shouldDisplay && !showKelleyBuysYourCar}
              theme={this.theme}
              showKelleyBuysYourCar={showKelleyBuysYourCar}
              isAppleWalletPassEnabled={isAppleWalletPassEnabled}
            />
          )}

          {showKelleyBuysYourCar && !this.isAdmin && (
            <kelley-buys-your-car
              slot="main"
              offerAmount={formattedFinalOfferAmount}
              shouldDisplay={showKelleyBuysYourCar}
              isFakeDoor={disposalType === DISPOSAL_TYPES.ICOANDKBYCFAKE}
              {...this.kelleyBuysYourCarOffer}
            />
          )}

          {!showKelleyBuysYourCar && (
            <Fragment>
              <accept-or-counteroffer slot="main" offerAmount={formattedFinalOfferAmount} {...this.acceptOrCounteroffer} />
              <key-factors slot="main" {...consumerMarketConfidence}></key-factors>
              {shouldDisplayTaxSavings && <tax-savings slot="main" potentialTaxSaving={potentialTaxSaving} />}

              <div class="break-before-page">
                <dealer-list
                  shouldDisplayDealerListInfo={shouldDisplayDealerListInfo}
                  allocatedDealers={allocated}
                  dealerOffers={dealerOffers}
                  buyingCenters={buyingCenters}
                  expirationDate={expirationDate}
                  dealerName={this.dealer.name}
                  offerId={offerId}
                  slot="main"
                  dealerAppointmentInfo={dealerAppointmentInfo}
                  showDealerChosenReasons={showDealerChosenReasons}
                  showDealerList={this.offer.showDealerList}
                  isAdmin={this.isAdmin}
                  ref={(el) => (this.dealerListElem = el as HTMLElement)}></dealer-list>
              </div>

              <additional-insights {...consumerMarketConfidence} />
              <div class={historyReportClass}>
                <expect-message {...{ shouldDisplayExpectDealership }} />
                <confident-vehicle-history-report
                  {...{
                    autoCheckFullReportLink,
                    reportGeneratedOnDate: reportDate,
                    vin,
                    shouldDisplayConfidentVehicleHistoryReport,
                    historyIcons,
                  }}
                />
              </div>
              <prepare-for-visit shouldDisplayPrepareForVisit={shouldDisplayPrepareForVisit} />
            </Fragment>
          )}

          <slot />

          <OfferDetailsModal shouldShowDealerComments={false} autoCheck={this.autoCheck} customer={this.customer} offer={this.offer} vehicle={this.vehicle} />

          {hasDealerBoost ? <offer-certificate-dealer-boost-disclaimer /> : <offer-certificate-disclaimer isKelleyBuysYourCarRequested={showKelleyBuysYourCar} />}
        </certificate-layout>
      </Host>
    );
  }
}
